import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Plus, Search } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCompanyMutation } from '@/hooks/useCompanyMutation'
import { supabase } from '@/integrations/supabase/client'
import { useCompany } from '@/lib/CompanyProvider'
import { CameraOCR } from './CameraOCR'

const orderSchema = z.object({
  plate: z.string().min(7, 'Placa inválida').max(8),
  vehicle_model: z.string().min(2, 'Modelo obrigatório'),
  vehicle_color: z.string().optional(),
  customer_name: z.string().min(2, 'Nome obrigatório'),
  customer_phone: z.string().min(8, 'Telefone obrigatório'),
  selected_services: z.array(z.string()).min(1, 'Selecione ao menos um serviço'),
  assigned_to: z.string().optional(),
})

type OrderFormValues = z.infer<typeof orderSchema>

export function CreateOrderModal() {
  const [open, setOpen] = useState(false)
  const [ocrOpen, setOcrOpen] = useState(false)
  const [detachCustomer, setDetachCustomer] = useState(false)
  const queryClient = useQueryClient()
  const { activeCompanyId } = useCompany()

  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      plate: '',
      vehicle_model: '',
      vehicle_color: '',
      customer_name: '',
      customer_phone: '',
      selected_services: [],
      assigned_to: '',
    },
  })

  const plateValue = form.watch('plate')

  // Auto-complete (busca inteligente por placa)
  const { data: vehicleData, isFetching: isSearchingPlate } = useQuery({
    queryKey: ['vehicleSearch', plateValue, activeCompanyId],
    queryFn: async () => {
      if (plateValue.length < 7) return null

      const { data, error } = await supabase
        .from('vehicles')
        .select(`
          id, model, color,
          customers (id, name, phone)
        `)
        .eq('plate', plateValue.replace('-', ''))
        .eq('company_id', activeCompanyId)
        .single()

      if (error || !data) return null
      return data
    },
    enabled: plateValue.length >= 7,
    staleTime: 1000 * 60, // 1 minuto
  })

  // Preenche o formulário se encontrar o veículo
  useEffect(() => {
    setDetachCustomer(false)
    if (vehicleData?.customers) {
      // Usa setValue silenciosamente sem disparar novos renders
      const customers = Array.isArray(vehicleData.customers)
        ? vehicleData.customers[0]
        : vehicleData.customers
      form.setValue('vehicle_model', vehicleData.model)
      form.setValue('vehicle_color', vehicleData.color || '')
      form.setValue('customer_name', customers?.name || '')
      form.setValue('customer_phone', customers?.phone || '')
    }
  }, [vehicleData, form])

  // Busca catálogo de serviços dinamicamente
  const { data: catalogServices, isLoading: isLoadingServices } = useQuery({
    queryKey: ['services', activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('company_id', activeCompanyId)
        .order('name')
      if (error) throw error
      return data
    },
    enabled: !!activeCompanyId && open,
  })

  // Busca colaboradores ativos
  const { data: collaborators } = useQuery({
    queryKey: ['collaborators', activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collaborators')
        .select('id, name')
        .eq('company_id', activeCompanyId)
        .eq('status', 'ATIVO')
        .order('name')
      if (error) throw error
      return data
    },
    enabled: !!activeCompanyId && open,
  })

  const createOrder = useCompanyMutation({
    mutationFn: async (variables: OrderFormValues & { company_id?: string }) => {
      const cleanPlate = variables.plate.replace('-', '')

      // 1. Resolve Cliente
      let customerId = null
      if (vehicleData && !detachCustomer) {
        customerId = Array.isArray(vehicleData?.customers)
          ? vehicleData?.customers[0]?.id
          : (vehicleData?.customers as any)?.id
      }

      if (!customerId) {
        const { data: newCustomer, error: custErr } = await supabase
          .from('customers')
          .insert({
            id: crypto.randomUUID(),
            company_id: variables.company_id,
            name: variables.customer_name,
            phone: variables.customer_phone,
          })
          .select('id')
          .single()
        if (custErr) throw custErr
        customerId = newCustomer.id
      }

      // 2. Resolve Veículo
      let vehicleId = vehicleData?.id
      if (!vehicleId) {
        const { data: newVehicle, error: vehErr } = await supabase
          .from('vehicles')
          .insert({
            id: crypto.randomUUID(),
            company_id: variables.company_id,
            customer_id: customerId,
            plate: cleanPlate,
            model: variables.vehicle_model,
            color: variables.vehicle_color || '',
          })
          .select('id')
          .maybeSingle()

        if (vehErr) {
          if (vehErr.code === '23505' || vehErr.message.includes('duplicate')) {
            // Placa já existe no banco (mesmo que para outra company ou bug no select)
            const { data: existing } = await supabase
              .from('vehicles')
              .select('id')
              .eq('plate', cleanPlate)
              .single()
            if (existing) {
              vehicleId = existing.id
            } else {
              throw vehErr
            }
          } else {
            throw vehErr
          }
        } else {
          vehicleId = newVehicle?.id
        }
      }

      // 3. Monta payload de Serviços e Total
      const selectedSvcObjs =
        catalogServices?.filter((s) => variables.selected_services.includes(s.id)) || []
      const total = selectedSvcObjs.reduce((acc, curr) => acc + Number(curr.price), 0)

      // A V1 guardava um JSON com { id, name, price, time_minutes }
      const servicesPayload = selectedSvcObjs.map((s) => ({
        id: s.id,
        name: s.name,
        price: Number(s.price),
        time_minutes: s.time_minutes,
      }))

      // 4. Cria a OS ou Orçamento
      const nowIso = new Date().toISOString()
      const isQuote = Boolean((variables as any).is_quote)
      const { error: osErr } = await supabase.from('orders').insert({
        id: crypto.randomUUID(),
        company_id: variables.company_id,
        customer_id: customerId,
        vehicle_id: vehicleId,
        plate: cleanPlate,
        customer_name: variables.customer_name,
        customer_phone: variables.customer_phone,
        vehicle_model: variables.vehicle_model,
        services: servicesPayload,
        total: total,
        assigned_to: variables.assigned_to || null,
        status: isQuote ? 'ORCAMENTO' : 'AGUARDANDO',
        is_quote: isQuote,
        stage_timestamps: isQuote ? { ORCAMENTO: nowIso } : { AGUARDANDO: nowIso },
        stage_durations_minutes: {},
      })

      if (osErr) throw osErr
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', activeCompanyId] })
      queryClient.invalidateQueries({ queryKey: ['quotes', activeCompanyId] })
      setOpen(false)
      form.reset()
    },
    onError: (error: any) => {
      console.error('Erro ao salvar:', error)
      alert(`Erro ao salvar: ${error.message || error.toString()}`)
    },
  })

  const onSubmit = (data: OrderFormValues, isQuote = false) => {
    createOrder.mutate({ ...data, is_quote: isQuote } as any)
  }

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (value.length > 7) value = value.substring(0, 7)
    if (value.length > 3) {
      value = `${value.substring(0, 3)}-${value.substring(3)}`
    }
    form.setValue('plate', value, { shouldValidate: true })
  }

  const handleOCRSuccess = (plate: string) => {
    form.setValue('plate', plate, { shouldValidate: true })
    // Focus para o usuário conferir se está certo antes de enviar
  }

  const toggleService = (serviceId: string) => {
    const current = form.getValues('selected_services')
    if (current.includes(serviceId)) {
      form.setValue(
        'selected_services',
        current.filter((id) => id !== serviceId),
        { shouldValidate: true }
      )
    } else {
      form.setValue('selected_services', [...current, serviceId], { shouldValidate: true })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2">
            <Plus size={18} />
            Nova OS
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-xl">Entrada de Veículo</DialogTitle>
          </DialogHeader>

          <form onSubmit={form.handleSubmit((data) => onSubmit(data, false))} className="space-y-4 mt-4">
            <div className="grid gap-2">
              <Label className="text-slate-300">Placa *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="ABC-1234"
                    {...form.register('plate')}
                    onChange={handlePlateChange}
                    className={`bg-slate-800 border-slate-700 uppercase font-mono text-lg tracking-wider ${isSearchingPlate ? 'pr-10' : ''}`}
                  />
                  {isSearchingPlate && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 size={16} className="animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={() => setOcrOpen(true)}
                  className="bg-slate-800 hover:bg-slate-700 border border-slate-600"
                  title="Escanear com a Câmera"
                >
                  <Camera size={18} className="text-blue-400" />
                </Button>
              </div>
              {form.formState.errors.plate && (
                <span className="text-red-400 text-xs">{form.formState.errors.plate.message}</span>
              )}
              {vehicleData && !isSearchingPlate && (
                <span className="text-emerald-400 text-xs flex items-center gap-1 mt-1 font-medium bg-emerald-500/10 p-2 rounded-md border border-emerald-500/20">
                  <Search size={14} /> Cliente e veículo encontrados.
                </span>
              )}
              {!vehicleData && !isSearchingPlate && plateValue.length >= 7 && (
                <span className="text-amber-400 text-xs flex items-center gap-1 mt-1 font-medium bg-amber-500/10 p-2 rounded-md border border-amber-500/20">
                  <Search size={14} /> Placa nova. Preencha os dados abaixo para cadastrar o cliente
                  automaticamente.
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label className="text-slate-300">Modelo do Veículo *</Label>
                <Input
                  placeholder="Ex: Corolla"
                  {...form.register('vehicle_model')}
                  className="bg-slate-800 border-slate-700"
                />
                {form.formState.errors.vehicle_model && (
                  <span className="text-red-400 text-xs">
                    {form.formState.errors.vehicle_model.message}
                  </span>
                )}
              </div>
              <div className="grid gap-2">
                <Label className="text-slate-300">Cor</Label>
                <Input
                  placeholder="Ex: Branco"
                  {...form.register('vehicle_color')}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label className="text-slate-300">Nome do Cliente *</Label>
                <Input
                  placeholder="João Silva"
                  {...form.register('customer_name')}
                  disabled={!!vehicleData && !detachCustomer}
                  className="bg-slate-800 border-slate-700 disabled:opacity-50"
                />
                {form.formState.errors.customer_name && (
                  <span className="text-red-400 text-xs">
                    {form.formState.errors.customer_name.message}
                  </span>
                )}
              </div>
              <div className="grid gap-2">
                <Label className="text-slate-300">WhatsApp *</Label>
                <Input
                  placeholder="(11) 99999-9999"
                  {...form.register('customer_phone')}
                  disabled={!!vehicleData && !detachCustomer}
                  className="bg-slate-800 border-slate-700 disabled:opacity-50"
                />
                {form.formState.errors.customer_phone && (
                  <span className="text-red-400 text-xs">
                    {form.formState.errors.customer_phone.message}
                  </span>
                )}
              </div>
            </div>

            {!!vehicleData && !detachCustomer && (
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setDetachCustomer(true)
                    form.setValue('customer_name', '')
                    form.setValue('customer_phone', '')
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                >
                  Não é o titular do veículo?
                </button>
              </div>
            )}

            <div className="grid gap-2">
              <Label className="text-slate-300 mt-2">Serviços *</Label>
              <div className="bg-slate-950 p-3 rounded-md border border-slate-800 max-h-[150px] overflow-y-auto flex flex-col gap-2">
                {isLoadingServices ? (
                  <div className="flex items-center justify-center py-4 text-slate-500">
                    <Loader2 size={16} className="animate-spin mr-2" /> Carregando catálogo...
                  </div>
                ) : catalogServices?.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-2">
                    Nenhum serviço cadastrado.
                  </div>
                ) : (
                  catalogServices?.map((service) => {
                    const isSelected = form.watch('selected_services').includes(service.id)
                    return (
                      <div
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-blue-600/20 border border-blue-500/50' : 'bg-slate-800 border border-slate-700 hover:bg-slate-700'}`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 rounded-sm border flex items-center justify-center ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-500'}`}
                          >
                            {isSelected && <div className="w-2 h-2 bg-white rounded-sm" />}
                          </div>
                          <span className="text-sm font-medium">{service.name}</span>
                        </div>
                        <span className="text-sm text-slate-300">
                          R$ {Number(service.price).toFixed(2)}
                        </span>
                      </div>
                    )
                  })
                )}
              </div>
              {form.formState.errors.selected_services && (
                <span className="text-red-400 text-xs">
                  {form.formState.errors.selected_services.message}
                </span>
              )}
            </div>

            <div className="grid gap-2">
              <Label className="text-slate-300">Responsável pelo Serviço</Label>
              <select
                {...form.register('assigned_to')}
                className="bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Selecionar Responsável (Opcional) --</option>
                {collaborators?.map((collab) => (
                  <option key={collab.id} value={collab.id}>
                    {collab.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="pt-4 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={createOrder.isPending}
                onClick={form.handleSubmit((data) => onSubmit(data, true))}
                className="border-slate-700 hover:bg-slate-800 text-slate-200"
              >
                Gerar Orçamento
              </Button>
              <Button
                type="button"
                disabled={createOrder.isPending}
                onClick={form.handleSubmit((data) => onSubmit(data, false))}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createOrder.isPending ? 'Salvando...' : 'Criar OS'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <CameraOCR open={ocrOpen} onOpenChange={setOcrOpen} onScanSuccess={handleOCRSuccess} />
    </>
  )
}
